import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

// Basin colors can be separate Shopify products because the cabinet already
// occupies all three options. Match every named option instead of using a
// sibling's first/default variant or assuming both products use the same order.
function zentrobadMatchingBasinVariant(optionNames, values, sibling) {
  const normalize = value => String(value ?? '').normalize('NFC').trim().toLowerCase();
  if (!Array.isArray(optionNames) || !Array.isArray(values) || !Array.isArray(sibling?.options) || !Array.isArray(sibling?.variants)) return null;
  if (optionNames.length !== sibling.options.length || values.length !== optionNames.length) return null;
  const positions = optionNames.map(name => sibling.options.findIndex(other => normalize(other) === normalize(name)));
  if (positions.some(position => position < 0) || values.some(value => !normalize(value))) return null;
  // An existing sold-out execution stays selected and shows its actual stock
  // state on the destination page; it must not silently become another cabinet.
  return sibling.variants.find(variant => positions.every((position, index) => normalize(variant.options?.[position]) === normalize(values[index]))) || null;
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {Array<{id: number|string, available: boolean, options: string[]}>} */
  #zentrobadVariants = [];

  /** @type {boolean} */
  #isResolvingSelection = false;

  connectedCallback() {
    super.connectedCallback();
    this.#sortDimensionOptions();
    this.#refreshRadioState();
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.#loadZentrobadVariants();
    this.#updateZentrobadAvailability();
    this.updateBasinLinks();
  }

  #refreshRadioState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (!this.#isResolvingSelection) {
      this.#loadZentrobadVariants();
      this.#resolveUnavailableSelection(event.target);
      this.#updateZentrobadAvailability();
    }

    const eventSelectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    // The first checked input is usually the width option. Prefer the element
    // that actually triggered the change so grip/color switches carry the
    // correct option value and variant ID into the request and URL.
    const selectedOption = eventSelectedOption || this.selectedOption;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.updateBasinLinks();
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#sortDimensionOptions();
    this.#refreshRadioState();
    this.updateVariantPickerCss();
    this.#loadZentrobadVariants();
    this.#updateZentrobadAvailability();
    this.updateBasinLinks();

    return newProduct;
  }

  updateBasinLinks() {
    const source = this.querySelector('[data-zentrobad-basin-configurations]');
    if (!source) return;
    let data;
    try { data = JSON.parse(source.textContent || '{}'); } catch { return; }
    const values = [...(data.selection || [])];
    this.querySelectorAll('fieldset[data-fieldset-index] input:checked, select[data-fieldset-index]').forEach(input => {
      const position = Number(input.dataset.fieldsetIndex);
      if (Number.isInteger(position)) values[position] = input.value;
    });
    let unavailable = false;
    this.querySelectorAll('[data-zentrobad-basin-handle]').forEach(link => {
      const sibling = data.siblings?.find(item => item.handle === link.dataset.zentrobadBasinHandle);
      const variant = zentrobadMatchingBasinVariant(data.options, values, sibling);
      if (variant && sibling.url) {
        link.setAttribute('href', `${sibling.url}${sibling.url.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variant.id)}`);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('title');
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('title', 'In dieser Konfiguration nicht verfügbar');
        unavailable = true;
      }
    });
    const status = this.querySelector('[data-zentrobad-basin-status]');
    if (status) status.hidden = !unavailable;
  }

  #sortDimensionOptions() {
    this.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
      const legendText = fieldset.querySelector('legend')?.textContent || '';
      if (!this.#isDimensionOptionName(legendText)) return;

      const labels = Array.from(fieldset.querySelectorAll(':scope > label.variant-option__button-label'));
      const sortable = labels
        .map((label, originalIndex) => ({
          label,
          originalIndex,
          value: this.#dimensionSortValue(label.querySelector('input')?.value || label.textContent || ''),
        }))
        .filter((item) => Number.isFinite(item.value));

      if (sortable.length < 2) return;

      sortable
        .sort((a, b) => a.value - b.value || a.originalIndex - b.originalIndex)
        .forEach((item, index) => {
          const input = item.label.querySelector('input');
          if (input instanceof HTMLInputElement) {
            input.dataset.inputIndex = String(index);
            input.dataset.inputId = `${input.dataset.fieldsetIndex || '0'}-${index}`;
          }
          fieldset.appendChild(item.label);
        });
    });

    this.querySelectorAll('.variant-option--dropdowns').forEach((wrapper) => {
      const labelText = wrapper.querySelector('label')?.textContent || '';
      const select = wrapper.querySelector('select');
      if (!(select instanceof HTMLSelectElement) || !this.#isDimensionOptionName(labelText)) return;

      const selectedValue = select.value;
      const options = Array.from(select.options)
        .map((option, originalIndex) => ({
          option,
          originalIndex,
          value: this.#dimensionSortValue(option.value || option.textContent || ''),
        }))
        .filter((item) => Number.isFinite(item.value));

      if (options.length < 2) return;

      options
        .sort((a, b) => a.value - b.value || a.originalIndex - b.originalIndex)
        .forEach((item) => select.appendChild(item.option));

      select.value = selectedValue;
    });
  }

  /**
   * @param {string} value
   */
  #isDimensionOptionName(value) {
    return /(^|\s)(ma[ßs]e?|größe|groesse|breite|höhe|hoehe|tiefe|size|width|height|depth)(\s|$|:)/i.test(value || '');
  }

  /**
   * @param {string} value
   */
  #dimensionSortValue(value) {
    const normalized = String(value || '').replace(',', '.');
    const match = normalized.match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : Number.NaN;
  }

  #loadZentrobadVariants() {
    const script = this.querySelector('[data-zentrobad-variants]');
    if (!script?.textContent) {
      this.#zentrobadVariants = [];
      return;
    }

    try {
      const parsed = JSON.parse(script.textContent);
      this.#zentrobadVariants = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Invalid Zentrobad variant data', error);
      this.#zentrobadVariants = [];
    }
  }

  /**
   * @param {HTMLElement} changedElement
   */
  #resolveUnavailableSelection(changedElement) {
    if (!this.#zentrobadVariants.length) return;

    const selectedValues = this.#selectedOptionTextValues();
    if (!selectedValues.length || this.#findAvailableVariant(selectedValues)) return;

    const changedIndex = this.#optionIndexForElement(changedElement);
    const fallback = this.#findBestAvailableVariant(selectedValues, changedIndex);
    if (!fallback) return;

    this.#isResolvingSelection = true;
    this.#selectVariantOptionValues(fallback.options);
    this.#isResolvingSelection = false;
  }

  /**
   * @param {string[]} selectedValues
   * @returns {{id: number|string, available: boolean, options: string[]} | null}
   */
  #findAvailableVariant(selectedValues) {
    return this.#zentrobadVariants.find((variant) => {
      return variant.available && selectedValues.every((value, index) => this.#normalize(variant.options?.[index]) === this.#normalize(value));
    }) || null;
  }

  /**
   * @param {string[]} selectedValues
   * @param {number} changedIndex
   * @returns {{id: number|string, available: boolean, options: string[]} | null}
   */
  #findBestAvailableVariant(selectedValues, changedIndex) {
    const available = this.#zentrobadVariants.filter((variant) => variant.available);
    if (!available.length) return null;

    const changedValue = selectedValues[changedIndex];
    const candidates = available.filter((variant) => {
      return changedIndex < 0 || this.#normalize(variant.options?.[changedIndex]) === this.#normalize(changedValue);
    });
    const pool = candidates.length ? candidates : available;

    return pool
      .map((variant) => ({
        variant,
        score: selectedValues.reduce((sum, value, index) => {
          if (index === changedIndex) return sum + 4;
          return sum + (this.#normalize(variant.options?.[index]) === this.#normalize(value) ? 1 : 0);
        }, 0),
      }))
      .sort((a, b) => b.score - a.score)[0]?.variant || null;
  }

  /**
   * @param {string[]} optionValues
   */
  #selectVariantOptionValues(optionValues) {
    optionValues.forEach((value, index) => {
      const input = this.querySelector(`fieldset[data-fieldset-index="${index}"] input[value="${CSS.escape(value)}"]`);
      if (input instanceof HTMLInputElement && !input.checked) {
        this.updateSelectedOption(input);
      }

      const select = this.querySelector(`select[data-fieldset-index="${index}"]`);
      if (select instanceof HTMLSelectElement && select.value !== value) {
        select.value = value;
        this.updateSelectedOption(select);
      }
    });
  }

  #updateZentrobadAvailability() {
    if (!this.#zentrobadVariants.length) return;

    const selectedValues = this.#selectedOptionTextValues();

    this.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.fieldsetIndex || -1);
      fieldset.querySelectorAll('input').forEach((input) => {
        const available = this.#hasAvailableVariantForValue(selectedValues, optionIndex, input.value);
        input.dataset.optionAvailable = String(available);
        input.setAttribute('aria-disabled', String(!available));
      });
    });

    this.querySelectorAll('select[data-fieldset-index]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const optionIndex = Number(select.dataset.fieldsetIndex || -1);
      Array.from(select.options).forEach((option) => {
        const available = this.#hasAvailableVariantForValue(selectedValues, optionIndex, option.value);
        option.dataset.optionAvailable = String(available);
        option.disabled = !available;
      });
    });
  }

  /**
   * @param {string[]} selectedValues
   * @param {number} optionIndex
   * @param {string} value
   */
  #hasAvailableVariantForValue(selectedValues, optionIndex, value) {
    if (optionIndex < 0) return true;
    return this.#zentrobadVariants.some((variant) => {
      if (!variant.available || this.#normalize(variant.options?.[optionIndex]) !== this.#normalize(value)) return false;
      return selectedValues.every((selectedValue, index) => {
        if (index === optionIndex || !selectedValue) return true;
        return this.#normalize(variant.options?.[index]) === this.#normalize(selectedValue);
      });
    });
  }

  #selectedOptionTextValues() {
    const values = [];

    this.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      if (checked instanceof HTMLInputElement) values[Number(checked.dataset.fieldsetIndex || values.length)] = checked.value;
    });

    this.querySelectorAll('select[data-fieldset-index]').forEach((select) => {
      if (select instanceof HTMLSelectElement) values[Number(select.dataset.fieldsetIndex || values.length)] = select.value;
    });

    return values.filter((value) => value !== undefined);
  }

  /**
   * @param {HTMLElement} element
   */
  #optionIndexForElement(element) {
    if (element instanceof HTMLInputElement) return Number(element.dataset.fieldsetIndex ?? -1);
    if (element instanceof HTMLSelectElement) return Number(element.dataset.fieldsetIndex ?? -1);
    return -1;
  }

  /**
   * @param {unknown} value
   */
  #normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
